/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { SortDirection } from "ag-grid-community";
import { catchError, map, Observable, of } from "rxjs";
import { CursorPaginationResponse } from "../../models/http/dashboard/cursor-pagination-response.model";
import { FunctionalUnitFilter } from "../../models/http/dashboard/functional-unit-filter.model";
import { LogoutResponse } from "../../models/http/user/logout-response.model";
import { Role } from "../../models/http/user/role.model";
import { UserInfo } from "../../models/http/user/user-info.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { PagedResponse } from "../../models/ui/paged-response.model";
import { TableResponse } from "../../models/ui/table-response.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class UsersHttpService {
	private _usersOperations = ENDPOINT_GROUPS.users.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves the list of user roles.
	 *
	 * @returns An observable that emits an array of user roles.
	 */
	public getUserRoles(): Observable<Role[]> {
		return this._communicationService.callService(
			this._usersOperations.getRoles.service,
			HttpMethods.GET
		);
	}

	/**
	 * Retrieves the user information.
	 *
	 * @returns An observable that emits the user information.
	 */
	public getUserInfo(): Observable<UserInfo> {
		return this._communicationService.callService(
			this._usersOperations.getUserInfo.service,
			HttpMethods.GET
		);
	}

	/**
	 * Retrieves a paginated list of users based on the specified criteria.
	 *
	 * @param tenantId - The ID of the tenant to which the users belong.
	 * @param page - The page number to retrieve.
	 * @param size - The number of users per page.
	 * @param sort - (Optional) The field by which to sort the users.
	 * @param filter - (Optional) A filter string to apply to the user search.
	 * @param role - (Optional) An array of roles to filter the users by.
	 * @param applications - (Optional) An array of applications to filter the users by.
	 * @param direction - (Optional) The direction of the sort, either "asc" or "desc".
	 * @returns An Observable that emits a PagedResponse containing UserInfo objects.
	 */
	public getUsers(
		tenantId: string,
		page: number,
		size: number,
		sort?: string,
		filter?: string,
		role?: string[],
		applications?: string[],
		direction?: SortDirection
	): Observable<TableResponse<PagedResponse<UserInfo>>> {
		const pathParams = { tenantId };
		const params = new HttpParams({
			fromObject: {
				page,
				size,
				...(sort && { sort }),
				...(filter && { filter }),
				...(role && { role }),
				...(applications && { applications }),
				...(direction && { direction })
			}
		});

		return this._communicationService
			.callService(this._usersOperations.getUsers.service, HttpMethods.GET, {
				pathParams,
				params
			})
			.pipe(
				map(response => ({ rowData: response, hasError: false })),
				catchError(() => of({ rowData: {} as PagedResponse<UserInfo>, hasError: true }))
			);
	}

	/**
	 * Adds a new user to the specified tenant.
	 *
	 * @param tenantId - The ID of the tenant to which the user will be added.
	 * @param user - The user information to be added.
	 * @returns An Observable that emits the ID of the newly added user.
	 */
	public addUser(tenantId: string, user: UserInfo): Observable<string> {
		const pathParams = { tenantId };

		return this._communicationService.callService(
			this._usersOperations.addUser.service,
			HttpMethods.POST,
			{ pathParams },
			user
		);
	}

	/**
	 * Retrieves a user by their ID.
	 *
	 * @param tenantId - The ID of the tenant to which the user belongs.
	 * @param userId - The ID of the user to retrieve.
	 * @returns An Observable that emits the full user details.
	 */
	public getUserById(tenantId: string, userId: number): Observable<UserInfo> {
		const pathParams = { tenantId, userId };

		return this._communicationService.callService(
			this._usersOperations.getUser.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Updates the user information for a specific tenant.
	 *
	 * @param tenantId - The ID of the tenant to which the user belongs.
	 * @param userId - The ID of the user to be updated.
	 * @param user - The updated user information.
	 * @returns An Observable that emits a string indicating the result of the update operation.
	 */
	public updateUser(tenantId: string, userId: number, user: UserInfo): Observable<string> {
		const pathParams = { tenantId, userId };

		return this._communicationService.callService(
			this._usersOperations.updateUser.service,
			HttpMethods.PUT,
			{ pathParams },
			user
		);
	}

	/**
	 * Deletes a user based on the provided tenant ID and user ID.
	 *
	 * @param tenantId - The ID of the tenant to which the user belongs.
	 * @param userId - The ID of the user to be deleted.
	 * @returns An Observable that emits a string indicating the result of the deletion operation.
	 */
	public deleteUser(tenantId: string, userId: number): Observable<string> {
		const pathParams = { tenantId, userId };

		return this._communicationService.callService(
			this._usersOperations.deleteUser.service,
			HttpMethods.DELETE,
			{ pathParams }
		);
	}

	/**
	 * Logs out the current user.
	 *
	 * @returns An Observable that emits the identity provider logout URL to navigate to.
	 */
	public logout(): Observable<LogoutResponse> {
		return this._communicationService.callService(
			this._usersOperations.logout.service,
			HttpMethods.POST,
			{},
			undefined,
			false
		);
	}

	/**
	 * Retrieves a cursor-paginated page of functional units.
	 *
	 * @param cursor - Optional opaque cursor from a previous response (stored in session storage).
	 * @param size - Number of items per page (default 20).
	 * @returns An Observable that emits a CursorPaginationResponse of FunctionalUnitFilter.
	 */
	public getFunctionalUnits(
		cursor?: string | null,
		size: number = 20
	): Observable<CursorPaginationResponse<FunctionalUnitFilter>> {
		const params = new HttpParams({
			fromObject: {
				size,
				...(cursor && { cursor })
			}
		});

		return this._communicationService.callService(
			this._usersOperations.getUsersFunctionalUnits.service,
			HttpMethods.GET,
			{ params }
		);
	}
}
